import { useContext, useEffect, useState } from 'react';
import { Modal } from 'antd';
import RemoteDepositCheckCapture from './RemoteDepositCheckCapture';
import RemoteDepositCheckReview from './RemoteDepositCheckReview';
import { ExtractClaimCheckResponse } from '../../types/RemoteDepositCheck.types';
import { useTranslation } from 'react-i18next';
import { Step, SubmitStatus } from '../../types/RemoteDepositCheck.types';
import { AuthContext } from '../../App';

function ClaimRemoteDepositCheck(props) {
    const { extendSession } = useContext(AuthContext);
    const [step, setStep] = useState<Step>('capture');
    const [extractedClaimCheckData, setExtractedClaimCheckData] = useState<ExtractClaimCheckResponse | null>(null);
    const { t } = useTranslation("ClaimDetails");
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('pending');

    useEffect(() => {
        // Extend the user's session when:
        // 1. This useEffect runs when the RemoteDepositCheck component initially mounts.
        // 2. The component does NOT unmount when switching between 'capture' and 'review' steps within the modal,
        //    so we include 'step' in the dependency array to ensure the effect re-executes each time the step changes.
        //    This allows us to extend the session as the user navigates through different parts of the workflow 
        //    (e.g., capture → review or review → capture). This keeps the session alive during user interaction.
        extendSession();
    }, [step]);
    
    function handleCancel() {
       // Always clear extracted data on cancel to ensure a clean state.
       // Whether the user cancels or starts over, we reset the extracted results to avoid stale data on next attempt.
       setExtractedClaimCheckData(null);
        // When the screen is in the inital capture screen or the final submission is successful, the close button will close the modal.
        // If the submission is failed or still pending for the extracted results to submit, the close button will not close the modal.
        // Instead, it will restart the flow by showing the initial capture screen while keeping the modal open.
        const shouldStartOver =
          (submitStatus === 'pending' || submitStatus === 'failed') &&
          !!extractedClaimCheckData;
        if (shouldStartOver) {
          // Restart the flow
          setStep('capture');
          setSubmitStatus('pending');
        } else {
          // Just close the modal
          props.toggleCheckDepositModal(false);
        }
      }
      
    return (
        <Modal open={true}
            title={t('ClaimDetails:btnClaimRemoteDepositCheck')}
            footer={null}
            onCancel={handleCancel}
            centered={true}
            destroyOnHidden={true}
        >
            <hr />
            {step === 'capture' && (
                <RemoteDepositCheckCapture
                    onExtracted={setExtractedClaimCheckData}
                    toggleModal={props.toggleCheckDepositModal}
                    onNext={() => setStep('review')}
                />
            )}
            {step === 'review' && extractedClaimCheckData && (
                <RemoteDepositCheckReview
                    extractedData={extractedClaimCheckData}
                    toggleModal={props.toggleCheckDepositModal}
                    onBack={() => setStep('capture')}
                    onSubmitResult={setSubmitStatus} // <-- child tells parent if submit succeeded or failed
                />
            )}
        </Modal>
    );
}

export default ClaimRemoteDepositCheck;
